import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Spin, Switch, Tag, Typography, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { API_ENDPOINTS } from '../../api/config';
import { get, put } from '../../api/request';

const { Paragraph, Text } = Typography;

interface ReaderAdvertisementSettings {
  enabled: boolean;
  provider: string;
  left_enabled: boolean;
  left_image_url: string;
  left_target_url: string;
  left_ad_unit_id: string;
  left_alt: string;
  right_enabled: boolean;
  right_image_url: string;
  right_target_url: string;
  right_ad_unit_id: string;
  right_alt: string;
  updated_at?: string | null;
}

const initialValues: ReaderAdvertisementSettings = {
  enabled: false,
  provider: 'pending',
  left_enabled: true,
  left_image_url: '',
  left_target_url: '',
  left_ad_unit_id: '',
  left_alt: '左侧广告',
  right_enabled: true,
  right_image_url: '',
  right_target_url: '',
  right_ad_unit_id: '',
  right_alt: '右侧广告',
};

const ReaderAdvertisementSettingsCard: React.FC = () => {
  const [form] = Form.useForm<ReaderAdvertisementSettings>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<ReaderAdvertisementSettings>(initialValues);
  const enabled = Form.useWatch('enabled', form) ?? false;
  const leftImageUrl = Form.useWatch('left_image_url', form) || '';
  const rightImageUrl = Form.useWatch('right_image_url', form) || '';

  useEffect(() => {
    get<ReaderAdvertisementSettings>(API_ENDPOINTS.system_app.readerAdvertisementSettings)
      .then((result) => {
        setSettings(result);
        form.setFieldsValue(result);
      })
      .catch((error) => message.error(error instanceof Error ? error.message : '广告设置加载失败'))
      .finally(() => setLoading(false));
  }, [form]);

  const saveSettings = async (values: ReaderAdvertisementSettings) => {
    setSaving(true);
    try {
      const result = await put<ReaderAdvertisementSettings>(
        API_ENDPOINTS.system_app.readerAdvertisementSettings,
        values,
      );
      setSettings(result);
      form.setFieldsValue(result);
      message.success('阅读页广告设置已保存');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '广告设置保存失败');
    } finally {
      setSaving(false);
    }
  };

  const renderSlot = (side: 'left' | 'right', title: string, imageUrl: string) => (
    <Card
      size="small"
      title={title}
      extra={(
        <Form.Item name={`${side}_enabled`} valuePropName="checked" noStyle>
          <Switch checkedChildren="展示" unCheckedChildren="隐藏" />
        </Form.Item>
      )}
      style={{ flex: '1 1 360px' }}
    >
      <Form.Item label="广告图片地址" name={`${side}_image_url`} extra="支持 http/https 地址，也支持站内 /api/... 相对地址。">
        <Input placeholder="暂无素材时保持为空" />
      </Form.Item>
      <Form.Item label="点击跳转地址" name={`${side}_target_url`} extra="可选；仅允许 http/https 地址。">
        <Input placeholder="https://..." />
      </Form.Item>
      <Form.Item label="平台广告位 ID" name={`${side}_ad_unit_id`} extra="预留给百度、腾讯等广告平台，确定平台后填入。">
        <Input placeholder="暂未接入平台，可留空" />
      </Form.Item>
      <Form.Item label="图片说明" name={`${side}_alt`}>
        <Input maxLength={80} />
      </Form.Item>
      {imageUrl && (
        <div style={{ width: 120, height: 240, overflow: 'hidden', border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
          <img src={imageUrl} alt={`${title}预览`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
    </Card>
  );

  return (
    <Card title="阅读页竖屏广告" bordered={false} style={{ maxWidth: 960, marginTop: 24 }}>
      <Spin spinning={loading}>
        <Alert
          type={enabled ? (leftImageUrl || rightImageUrl ? 'success' : 'warning') : 'info'}
          showIcon
          style={{ marginBottom: 24 }}
          message={enabled ? '阅读页广告总开关已开启' : '阅读页广告默认关闭'}
          description={enabled && !leftImageUrl && !rightImageUrl
            ? '当前没有广告素材，读者端仍不会显示空广告位；确定广告平台后可继续接入平台适配器。'
            : '广告只在大屏阅读页两侧显示；平板和手机自动隐藏，不影响正文阅读。'}
        />
        <Form form={form} layout="vertical" initialValues={initialValues} onFinish={saveSettings}>
          <Space size="large" wrap style={{ marginBottom: 18 }}>
            <Form.Item label="广告总开关" name="enabled" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch checkedChildren="已开启" unCheckedChildren="已关闭" />
            </Form.Item>
            <Form.Item label="广告平台标识" name="provider" style={{ width: 260, marginBottom: 0 }} extra="未确定平台填 pending；自有图片填 custom。">
              <Input placeholder="pending / custom / 平台代码" maxLength={50} />
            </Form.Item>
          </Space>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 16, flexWrap: 'wrap' }}>
            {renderSlot('left', '左侧竖屏广告位', leftImageUrl)}
            {renderSlot('right', '右侧竖屏广告位', rightImageUrl)}
          </div>
          <Form.Item style={{ marginTop: 22, marginBottom: 12 }}>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
              保存广告设置
            </Button>
          </Form.Item>
        </Form>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          当前状态：<Tag color={settings.enabled ? 'green' : 'default'}>{settings.enabled ? '开启' : '关闭'}</Tag>
          平台：<Text strong>{settings.provider || 'pending'}</Text>
          {settings.updated_at ? `，最后更新于 ${new Date(settings.updated_at).toLocaleString('zh-CN', { hour12: false })}` : '（系统默认值）'}
        </Paragraph>
      </Spin>
    </Card>
  );
};

export default ReaderAdvertisementSettingsCard;
